import { passwordSchema } from "@pcj/shared";
import { type FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Banner, Button, Field, inputClass } from "../../components/ui";
import { authClient, authErrorMessage } from "../../lib/auth-client";
import { AuthLayout } from "./AuthLayout";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const { error: requestError } = await authClient.requestPasswordReset({ email, redirectTo: "/reset-password" });
    setBusy(false);
    if (requestError) setError(authErrorMessage(requestError));
    else setSent(true);
  }

  return (
    <AuthLayout title="Restablecer contraseña" subtitle="Te enviaremos un enlace para elegir una nueva.">
      {sent ? (
        // Same answer whether or not the account exists (no email enumeration).
        <Banner tone="success">Si existe una cuenta con ese correo, te enviamos un enlace. Vence en 1 hora.</Banner>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <Field label="Correo electrónico">
            {(id) => (
              <input
                id={id}
                type="email"
                autoComplete="email"
                required
                className={inputClass}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          {error && <Banner tone="error">{error}</Banner>}
          <Button type="submit" loading={busy} className="w-full">
            Enviar enlace
          </Button>
        </form>
      )}
      <p className="mt-4 text-xs text-gray-600">
        <Link to="/login" className="underline hover:text-gray-900">
          Volver a iniciar sesión
        </Link>
      </p>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!token || params.get("error")) {
    return (
      <AuthLayout title="Enlace no válido">
        <Banner tone="error">El enlace no es válido o ya venció.</Banner>
        <p className="mt-4 text-xs text-gray-600">
          <Link to="/forgot-password" className="underline hover:text-gray-900">
            Solicitar un nuevo enlace
          </Link>
        </p>
      </AuthLayout>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? "Contraseña inválida");
    if (password !== confirm) return setError("Las contraseñas no coinciden.");
    setBusy(true);
    const { error: resetError } = await authClient.resetPassword({ newPassword: password, token: token as string });
    setBusy(false);
    if (resetError) return setError(authErrorMessage(resetError));
    navigate("/login?reset=1", { replace: true });
  }

  return (
    <AuthLayout title="Elige una nueva contraseña">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Nueva contraseña" hint="Mínimo 10 caracteres.">
          {(id) => (
            <input
              id={id}
              type="password"
              autoComplete="new-password"
              required
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        <Field label="Confirma la contraseña">
          {(id) => (
            <input
              id={id}
              type="password"
              autoComplete="new-password"
              required
              className={inputClass}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          )}
        </Field>
        {error && <Banner tone="error">{error}</Banner>}
        <Button type="submit" loading={busy} className="w-full">
          Guardar contraseña
        </Button>
      </form>
    </AuthLayout>
  );
}
